import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowRight, Droplets, FileText, Sparkles, Mail } from "lucide-react";
import api from "@/lib/api";
import { trackEvent } from "@/lib/pwsTracking";
import { Button } from "@/components/ui/button";
import { BOOKING } from "@/constants/testIds";
import { BrandLogo } from "@/components/BrandLogo";

const MAX_POLL = 12;
const POLL_INTERVAL_MS = 2000;
const apiBase = process.env.REACT_APP_BACKEND_URL || "";

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [status, setStatus] = useState("pending");  // pending | paid | failed | timeout
  const [reference, setReference] = useState(params.get("ref") || null);
  const [pollCount, setPollCount] = useState(0);
  // Jun 2026 — In-Field Technician Booking: when the blob is present the
  // tech came from an inspection; offer a one-tap return to that screen.
  // Only honoured when the blob belongs to THIS checkout session (or this
  // booking reference) — a stale blob from an earlier field booking must
  // never leak into a later normal customer booking on the same device.
  const [fieldReturn] = useState(() => {
    try {
      const blob = JSON.parse(localStorage.getItem("aware_field_booking") || "null");
      if (!blob?.appointment_id) return null;
      const matchesSession = blob.session_id && sessionId && blob.session_id === sessionId;
      const matchesRef = blob.reference && params.get("ref") && blob.reference === params.get("ref");
      if (!matchesSession && !matchesRef) {
        // Stale/unrelated blob: ignore here; purge only if clearly expired
        // so an in-flight field booking on this device isn't disturbed.
        if ((Date.now() - (blob.ts || 0)) > 2 * 60 * 60 * 1000) {
          localStorage.removeItem("aware_field_booking");
        }
        return null;
      }
      return blob;
    } catch { return null; }
  });
  const returnToInspection = () => {
    localStorage.removeItem("aware_field_booking");
    navigate(`/field/inspection/${fieldReturn.appointment_id}`);
  };

  useEffect(() => {
    if (!sessionId) { navigate("/"); return; }

    let cancelled = false;
    let attempts = 0;
    let paymentFired = false;

    const firePurchase = (data) => {
      // In-memory guard — same mount, repeated polling ticks.
      if (paymentFired) return;
      paymentFired = true;
      if (!data.reference) return;
      // Feb 2026, revised — this guard must stop a plain refresh/repeat
      // poll from double-firing (the common, expected case), but must
      // NOT be able to permanently bury a purchase GA4 never actually
      // received — e.g. analytics consent wasn't granted yet, an
      // ad-blocker silently dropped the request, or the network hiccuped
      // between here and the GTM/GA4 endpoint. `sessionStorage` (not
      // `localStorage`) is the right scope for that: it covers the
      // whole "this browsing session" — every refresh and every re-poll
      // on this tab — but clears the moment the tab/browser closes, so a
      // later, genuinely new visit to this confirmation link (next day,
      // new tab, consent granted this time) gets a fresh attempt.
      // `transaction_id` stays identical on every attempt specifically
      // so GA4's own documented server-side dedup for the `purchase`
      // event (by transaction_id) can safely collapse it if the earlier
      // attempt DID land — see support.google.com/analytics/answer/12313109.
      // Remaining limitation: if an ad-blocker blocks the request on
      // every single visit, no client-side guard can detect or recover
      // that — there is no delivery acknowledgement to check against.
      const dedupeKey = `aware_purchase_tracked:${data.reference}`;
      try {
        if (sessionStorage.getItem(dedupeKey)) return;
        sessionStorage.setItem(dedupeKey, "1");
      } catch { /* private-mode storage — paymentFired above still guards this mount */ }
      try {
        // Phase 27 — booking_completed tracking event (fail-soft).
        trackEvent("booking_completed", {
          bookingReference: data.reference || null,
        });
      } catch { /* never block UI on analytics */ }
      try {
        // Feb 2026 — GA4's `value` must be ex-GST product revenue only
        // (sum of item price x quantity); GST is reported separately via
        // `tax`, never folded into `value`. The backend now does this
        // split (AU GST, back-calculated from the GST-inclusive Stripe
        // total — see payments.py `payment_status`) so the website
        // doesn't duplicate that business rule; fall back to the old
        // all-inclusive amount only if an older backend response
        // (without the new fields) is ever hit.
        const hasGstSplit = typeof data.ex_gst_value === "number";
        const value = hasGstSplit ? data.ex_gst_value
          : Number(data.amount_total ? data.amount_total / 100 : 0) || 0;
        const tax = hasGstSplit ? data.gst_value : 0;
        const currency = (data.currency || "AUD").toUpperCase();
        // Feb 2026, revised — the backend now reconciles the real
        // Stripe line items for this session (main test + any posted-
        // kit/partner-kit-postage line), so an order with an add-on
        // reports every line in `items[]` instead of a single
        // synthetic one. Falls back to one item built from
        // test_id/test_name only if the backend ever omits `items`
        // (shouldn't happen on a paid booking, but never block the
        // event over it).
        const items = Array.isArray(data.items) && data.items.length
          ? data.items
          : [{
            item_id: data.test_id ? `aware-${data.test_id}`.toLowerCase() : (data.reference || null),
            item_name: data.test_name || "Water Test Booking",
            price: value,
            quantity: 1,
          }];
        // Feb 2026 — ONE authoritative purchase event (previously two
        // competing dataLayer pushes — a nested-`ecommerce` "purchase_completed"
        // and a flat "purchase" — plus a dead `window.gtag(...)` call that
        // is never defined under the current GTM-container-only install
        // (no gtag.js is loaded on this site; GTM owns the dataLayer).
        // This single "purchase" event carries BOTH the flat
        // transaction_id/value/currency fields (for simple Data Layer
        // Variables, e.g. a Google Ads conversion value variable) AND the
        // nested `ecommerce` object GA4's "Send Ecommerce Data" GTM
        // setting reads. Clearing `ecommerce` first is the documented
        // GA4/GTM pattern so a tag never re-reads a stale previous object.
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: "purchase",
          transaction_id: data.reference,
          value,
          currency,
          ecommerce: {
            transaction_id: data.reference,
            value,
            tax,
            shipping: 0,
            currency,
            items,
          },
        });
        // GTM form-conversion event. Fires ONLY after Stripe confirms
        // payment (paid branch of the polling loop); no PII. Unrelated
        // to the ecommerce/purchase schema above — left as-is.
        window.dataLayer.push({
          event: "booking_form_submit",
          form_name: "water_test_booking",
          form_type: "booking",
          booking_type: "water_test_online",
          test_type: data.test_id || data.test_name || null,
          booking_reference: data.reference || null,
          page_path: window.location.pathname,
          submission_status: "success",
        });
      } catch { /* never block UI on analytics */ }
    };

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      setPollCount(attempts);
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.reference) setReference(data.reference);
        if (data.payment_status === "paid") {
          firePurchase(data);
          setStatus("paid"); return;
        }
        if (data.status === "expired" || data.payment_status === "failed") { setStatus("failed"); return; }
        if (attempts >= MAX_POLL) { setStatus("timeout"); return; }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        if (attempts >= MAX_POLL) { setStatus("timeout"); return; }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, navigate]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <BrandLogo to="/" size="sm" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="bg-white rounded-3xl border border-slate-200 p-9 lg:p-12 shadow-sm text-center">
          {status === "pending" && (
            <>
              <div className="mx-auto w-16 h-16 rounded-2xl bg-cyan-50 flex items-center justify-center mb-5">
                <div className="w-7 h-7 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
              </div>
              <h1 className="font-display text-2xl lg:text-3xl font-bold text-slate-900">Confirming your payment...</h1>
              <p className="mt-2 text-slate-600">This usually takes a few seconds. Please don't close this page.</p>
              <p className="mt-1 text-xs text-slate-400">Attempt {pollCount} of {MAX_POLL}</p>
            </>
          )}

          {status === "paid" && (
            <>
              <div className="mx-auto w-20 h-20 rounded-full bg-emerald-500/15 flex items-center justify-center mb-6">
                <CheckCircle2 className="w-10 h-10 text-emerald-600" strokeWidth={2.5} />
              </div>
              <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900">Payment successful</h1>
              <p className="mt-3 text-slate-600">Your prepaid water test booking is confirmed. We've emailed your sample labels, collection instructions, and lab drop-off details.</p>

              {reference && (
                <div className="mt-7 inline-block px-6 py-4 rounded-2xl bg-slate-900 text-white">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-cyan-400 font-bold">Booking reference</div>
                  <div data-testid={BOOKING.confirmationRef} className="font-display text-2xl font-bold mt-1 tracking-wide">{reference}</div>
                </div>
              )}

              <div className="mt-8 grid sm:grid-cols-3 gap-3 text-left">
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
                  <FileText className="w-5 h-5 text-cyan-500 mb-2" />
                  <div className="font-semibold text-slate-900 text-sm">Sample label</div>
                  <div className="text-xs text-slate-500">Print + stick on bottle</div>
                </div>
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
                  <Droplets className="w-5 h-5 text-cyan-500 mb-2" />
                  <div className="font-semibold text-slate-900 text-sm">Collect sample</div>
                  <div className="text-xs text-slate-500">Clean 500ml bottle</div>
                </div>
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
                  <Sparkles className="w-5 h-5 text-cyan-500 mb-2" />
                  <div className="font-semibold text-slate-900 text-sm">Get answers</div>
                  <div className="text-xs text-slate-500">Usually within 1–3 days</div>
                </div>
              </div>

              {/* Taking-your-sample instructions */}
              <div data-testid="booking-success-sample-instructions" className="mt-8 text-left rounded-3xl bg-slate-50 border border-slate-200 p-6 lg:p-7">
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-cyan-700">Taking your water sample</div>
                <ul className="mt-3 space-y-2 text-sm text-slate-700 leading-relaxed">
                  <li className="flex gap-2"><span className="text-cyan-500 mt-0.5">•</span><span>Use a clean 500ml plastic or glass bottle if possible.</span></li>
                  <li className="flex gap-2"><span className="text-cyan-500 mt-0.5">•</span><span><strong>Collect from the kitchen tap where possible</strong> — results then reflect the water you actually drink and use.</span></li>
                  <li className="flex gap-2"><span className="text-cyan-500 mt-0.5">•</span><span>Rinse the bottle 2–3 times with the water being sampled before filling.</span></li>
                  <li className="flex gap-2"><span className="text-cyan-500 mt-0.5">•</span><span>Avoid touching the inside of the lid or bottle while filling.</span></li>
                  <li className="flex gap-2"><span className="text-cyan-500 mt-0.5">•</span><span>Fill the bottle to the brim at your sampling point.</span></li>
                  <li className="flex gap-2"><span className="text-cyan-500 mt-0.5">•</span><span>Seal tightly, refrigerate, and drop off within 24 hours of collection.</span></li>
                </ul>
                <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                  <strong>One booking = one sample.</strong> Your PDF includes a primary label and an optional spare in case the first is damaged. Only attach <em>one</em> label to the bottle you submit.
                </div>
                <div className="mt-2 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                  <strong>No printer?</strong> Simply write your booking reference {reference ? <span className="font-bold">{reference}</span> : "above"} clearly on the bottle with a permanent marker.
                </div>
                {reference && (
                  <a
                    data-testid="booking-success-labels-pdf"
                    href={`${apiBase}/api/bookings/${reference}/labels.pdf`}
                    target="_blank" rel="noreferrer"
                    className="mt-4 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-gradient-to-r from-cyan-500 to-sky-500 text-white text-sm font-semibold hover:opacity-90"
                  >
                    <FileText className="w-4 h-4" /> Download sample label (PDF)
                  </a>
                )}
              </div>

              {fieldReturn && (
                <div data-testid="field-booking-success-panel" className="mt-8 rounded-2xl bg-emerald-50 border border-emerald-300 p-5 text-left">
                  <div className="text-sm font-bold text-emerald-800">In-Field Booking complete</div>
                  <p className="mt-1 text-xs text-emerald-700">The lab test is locked in. Head back to the inspection to see the booking recorded.</p>
                  <Button data-testid="return-to-inspection-btn" onClick={returnToInspection} size="lg" className="mt-3 w-full rounded-full bg-emerald-600 hover:bg-emerald-700">
                    Return to Inspection
                  </Button>
                </div>
              )}
              <div className="mt-8">
                <Button data-testid={BOOKING.homeBtn} onClick={() => navigate("/")} size="lg" className="rounded-full bg-slate-900 hover:bg-slate-800">
                  Back to home
                </Button>
              </div>

              {/* Jun 2026 — $99/yr membership retired. The free Water Health
                  Record is included with every test, so point customers at it. */}
              <div data-testid="booking-success-record-panel" className="mt-10 text-left rounded-3xl bg-gradient-to-br from-cyan-50 via-white to-slate-50 border border-cyan-200 p-6 lg:p-7">
                <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-cyan-700">
                  <Sparkles className="w-3.5 h-3.5" /> Included with your test
                </div>
                <h3 className="font-display mt-3 text-xl lg:text-2xl font-bold text-slate-900">Your AWARE Water Health Record</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                  Every completed test starts or updates your property&apos;s permanent Water Health Record — reports, Health Score™, trends and retest reminders in one place, free.
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Link
                    to="/my-water"
                    className="btn-aqua inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold"
                  >
                    View My Water <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>

              <p className="mt-6 text-xs text-slate-400 inline-flex items-center gap-1.5"><Mail className="w-3 h-3" /> Deliver samples to: 1/37 Gateway Drive, Noosaville QLD 4566</p>
            </>
          )}

          {(status === "failed" || status === "timeout") && (
            <>
              <h1 className="font-display text-2xl lg:text-3xl font-bold text-slate-900">{status === "failed" ? "Payment didn't go through" : "Still checking..."}</h1>
              <p className="mt-2 text-slate-600">
                {status === "failed" ? "We couldn't confirm your payment. No charge was made." : "We couldn't confirm your payment in time. Please check your email for confirmation."}
              </p>
              <div className="mt-7 flex justify-center gap-3">
                <Button onClick={() => navigate("/book")} size="lg" className="rounded-full btn-aqua">
                  Try again <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
                <Button onClick={() => navigate("/")} size="lg" variant="outline" className="rounded-full">
                  Back to home
                </Button>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
